import React, { Component } from 'react';
import classNames from 'classnames';
import Select, { createFilter } from 'react-select';
import { EMAIL_REGEX } from '@/store/models/email';
import { submitCampaignPageLead } from '@/store/actions/campaignPages.actions';
import styles from './LeadCaptureForm.module.scss';
import { STATES } from '@/store/models/profile';
import { LICENSED_AGENTS } from '@/store/models/contact';
import { getThemeColor, PAGE_THEME_DEFAULTS } from '@/components/CampaignPages/utils/pageTheme';

const ALL_FIELDS = [
  { key: 'first_name', label: 'First Name', type: 'text', required: true },
  { key: 'last_name', label: 'Last Name', type: 'text', required: true },
  { key: 'email', label: 'Email', type: 'email', required: true },
  { key: 'phone', label: 'Phone', type: 'number' },
  { key: 'state', label: 'State', type: 'state' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'zip', label: 'Zip Code', type: 'text' },
  { key: 'license_status', label: 'License Status', type: 'dropdown' },
  { key: 'resume', label: 'Attachment', type: 'file' }
];

const SELECT_STYLES_LARGE = {
  control: base => ({ ...base, fontSize: '1.5rem' }),
  option: base => ({ ...base, fontSize: '1.5rem' })
};

const SELECT_STYLES_FLAT = {
  control: base => ({ ...base, fontSize: '14px', minHeight: '42px' }),
  option: base => ({ ...base, fontSize: '14px' })
};

const getFormErrorMessage = error => {
  const { jsonResponse } = error || {};
  if (!jsonResponse) return 'Network error. Please try again.';

  const { errors } = jsonResponse;
  if (Array.isArray(errors)) return errors.join(', ');
  if (errors) return Object.values(errors).flat().join(', ');

  return jsonResponse.message || jsonResponse.error || 'Submission failed. Please try again.';
};

class LeadCaptureForm extends Component {
  fileInputRefs = {};
  formBodyRef = React.createRef();
  redirectTimer = null;

  state = {
    form: {},
    submitting: false,
    fieldErrors: {},
    formError: null,
    consentChecked: false,
    collapsing: false,
    submitted: false,
    successMessage: null,
    formHeight: null
  };

  get fields() {
    const enabled = this.props.page.enabled_fields || ['first_name', 'last_name', 'email'];
    return ALL_FIELDS.filter(f => enabled.includes(f.key));
  }

  setField = (key, value) =>
    this.setState(({ form, fieldErrors }) => ({
      form: { ...form, [key]: value },
      fieldErrors: { ...fieldErrors, [key]: null }
    }));

  clearFile = key => {
    if (this.fileInputRefs[key]) this.fileInputRefs[key].value = '';
    this.setState(({ form, fieldErrors }) => {
      const next = { ...form };
      delete next[key];
      return { form: next, fieldErrors: { ...fieldErrors, [key]: null } };
    });
  };

  validate = () => {
    const { form } = this.state;
    const fieldErrors = {};
    this.fields.forEach(f => {
      if (f.required && !String(form[f.key] || '').trim()) fieldErrors[f.key] = 'Field is required';
    });
    if (form.email && !EMAIL_REGEX.test(form.email)) fieldErrors.email = 'Invalid email address format';
    return fieldErrors;
  };

  onSuccess = () => {
    const { redirect_url, thank_you_message } = this.props.page;
    const message = thank_you_message || 'Thank you! We will be in touch shortly.';
    const prefersReducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.setState({ submitted: true, successMessage: message }, () => this.scheduleRedirect(redirect_url));
      return;
    }

    const formHeight = this.formBodyRef.current?.scrollHeight ?? 0;

    this.setState({ successMessage: message, formHeight }, () => {
      requestAnimationFrame(() => {
        this.setState({ collapsing: true });
      });
    });
  };

  scheduleRedirect = redirectUrl => {
    if (!redirectUrl) return;
    this.redirectTimer = setTimeout(() => {
      window.location.href = redirectUrl;
    }, 3000);
  };

  handleFormCollapseEnd = e => {
    if (e.target !== this.formBodyRef.current || e.propertyName !== 'height') return;
    if (!this.state.collapsing || this.state.submitted) return;

    this.setState({ collapsing: false, submitted: true }, () => {
      this.scheduleRedirect(this.props.page.redirect_url);
    });
  };

  componentWillUnmount() {
    if (this.redirectTimer) clearTimeout(this.redirectTimer);
  }

  handleSubmit = async e => {
    e.preventDefault();
    if (!this.state.consentChecked) return;

    const fieldErrors = this.validate();
    if (Object.keys(fieldErrors).length) return this.setState({ fieldErrors, formError: null });

    this.setState({ submitting: true, fieldErrors: {}, formError: null });

    const formData = new FormData();
    Object.entries(this.state.form).forEach(([k, v]) => v && formData.append(k, v));

    try {
      await submitCampaignPageLead(this.props.slug, formData);
      this.onSuccess();
    } catch (error) {
      this.setState({ formError: getFormErrorMessage(error) });
    } finally {
      this.setState({ submitting: false });
    }
  };

  renderFileInput = (field, { form, error, preview }) => (
    <div
      className={classNames('input is-large', styles.fileDropzone, {
        [styles.fileDropzoneFilled]: form[field.key],
        'is-danger': error
      })}
      onClick={() => !preview && this.fileInputRefs[field.key]?.click()}
      onKeyDown={e => e.key === 'Enter' && !preview && this.fileInputRefs[field.key]?.click()}
      role="button"
      tabIndex={preview ? -1 : 0}
    >
      <input
        ref={el => {
          this.fileInputRefs[field.key] = el;
        }}
        id={field.key}
        type="file"
        className={styles.fileInputHidden}
        accept=".pdf,.doc,.docx,.txt"
        onChange={e => this.setField(field.key, e.target.files[0])}
        disabled={preview}
      />
      {form[field.key] ? (
        <div className={styles.fileSelected} onClick={e => e.stopPropagation()}>
          <i className={classNames('fas fa-file-alt', styles.fileIcon)} aria-hidden="true" />
          <span className={styles.fileName}>{form[field.key].name}</span>
          {!preview && (
            <button
              type="button"
              className={styles.fileRemove}
              aria-label={`Remove ${field.label}`}
              onClick={e => {
                e.stopPropagation();
                this.clearFile(field.key);
              }}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
        </div>
      ) : (
        <div className={styles.filePlaceholder}>
          <i className="fas fa-cloud-upload-alt" aria-hidden="true" />
          <span>Drag & drop or click to upload (resume, license, etc.)</span>
        </div>
      )}
    </div>
  );

  renderFieldInput = (field, { form, error, preview }) => {
    if (field.type === 'file') return this.renderFileInput(field, { form, error, preview });
    if (field.type === 'state') {
      return (
        <Select
          inputId={field.key}
          instanceId="campaign-lead-state"
          name={field.key}
          className={classNames('custom-select', { 'is-danger': error })}
          classNamePrefix="custom-select"
          styles={this.props.flat ? SELECT_STYLES_FLAT : SELECT_STYLES_LARGE}
          options={STATES}
          value={STATES.find(({ value }) => value === (form[field.key] || ''))}
          onChange={obj => this.setField(field.key, obj?.value || '')}
          filterOption={createFilter({ matchFrom: 'start' })}
          isDisabled={preview}
        />
      );
    }
    if (field.key === 'license_status') {
      const options = LICENSED_AGENTS(this.props.page?.user).filter(option => option.value !== 'unknown');
      return (
        <Select
          inputId={field.key}
          instanceId="campaign-lead-license-status"
          name={field.key}
          className={classNames('custom-select', { 'is-danger': error })}
          classNamePrefix="custom-select"
          styles={this.props.flat ? SELECT_STYLES_FLAT : SELECT_STYLES_LARGE}
          options={options}
          value={options.find(({ value }) => value === (form[field.key] || ''))}
          onChange={obj => this.setField(field.key, obj?.value || '')}
          isDisabled={preview}
          isClearable
        />
      );
    }
    return (
      <input
        id={field.key}
        className={classNames('input is-large', { 'is-danger': error })}
        type={field.type}
        value={form[field.key] || ''}
        onChange={e => this.setField(field.key, e.target.value)}
        disabled={preview}
      />
    );
  };

  render() {
    const { page, preview, flat } = this.props;
    const {
      form,
      submitting,
      fieldErrors,
      formError,
      consentChecked,
      collapsing,
      submitted,
      successMessage,
      formHeight
    } = this.state;

    const formBodyStyle = formHeight != null ? { height: collapsing || submitted ? 0 : formHeight } : undefined;

    return (
      <div className={classNames(styles.form, flat && styles.formFlat)}>
        {!submitted && (
          <div
            ref={this.formBodyRef}
            className={classNames(styles.formBody, collapsing && styles.formBodyCollapsing)}
            style={formBodyStyle}
            onTransitionEnd={this.handleFormCollapseEnd}
          >
            <form onSubmit={this.handleSubmit} noValidate className={styles.formInner}>
              <div className={styles.grid}>
                {this.fields.map(field => {
                  const error = fieldErrors[field.key];
                  const isFullWidth = field.type === 'file';
                  return (
                    <div key={field.key} className={classNames('field', styles.field, isFullWidth && styles.full)}>
                      <div className="control">
                        <label htmlFor={field.key} className={field.required ? 'required-field' : undefined}>
                          {field.required && <p className="astericks text-3xl">*</p>}
                          <p className="head text-2xl">{field.label}</p>
                        </label>
                        {this.renderFieldInput(field, { form, error, preview })}
                        {error && <p className="help is-danger error">{error}</p>}
                      </div>
                    </div>
                  );
                })}

                <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

                <div className={classNames('field', styles.consent, styles.full)}>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={e => this.setState({ consentChecked: e.target.checked })}
                      disabled={preview}
                    />{' '}
                    I consent to be contacted via phone, email, and text message.
                  </label>
                </div>

                {formError && (
                  <div className={classNames('notification is-danger is-light is-size-7', styles.full)}>
                    {formError}
                  </div>
                )}

                <div className={classNames('field', styles.full, styles.submitWrap)}>
                  <button
                    className={classNames('button is-medium', styles.ctaButton, { 'is-loading': submitting })}
                    type="submit"
                    disabled={submitting || !consentChecked || preview}
                    style={{
                      backgroundColor: getThemeColor(page, 'cta_color') || PAGE_THEME_DEFAULTS.cta_color,
                      borderColor: getThemeColor(page, 'cta_color') || PAGE_THEME_DEFAULTS.cta_color,
                      color: 'white'
                    }}
                  >
                    {page.cta_text || 'Apply Now'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {submitted && successMessage && (
          <div className={styles.successView}>
            <div className={classNames('notification is-success is-light', styles.successMessage)}>
              {successMessage}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default LeadCaptureForm;
